import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import Analytics from 'arbitrip-common/client/analytics';
import Config from 'arbitrip-common/client/utils/Config';

import DealActions from '@/actions/DealActions';
import LazyRecheckActions from '@/actions/LazyRecheckActions';
import BasketActions from '@/actions/BasketActions';
import HotelStore from '@/stores/HotelStore';
import ResultsStore from '@/stores/ResultsStore';
import RouterWrapper from '@/utils/RouterWrapper';
import { isAnonymousBlocked, redirectToLogin } from '@/utils/App';

const PERMISSION_PERFORM_RESERVATION = 'perform_reservation';

function destinationWithHotel(searchTerms, hotel) {
    return {
        ...searchTerms.destination,
        place: {
            ...searchTerms.destination?.place,
            ...(hotel?.id && { hotel_id: hotel.id }),
            ...(hotel?.name && { hotel_name: hotel.name }),
            ...(hotel?.address && { address: hotel.address }),
            ...(hotel?.city && { city: hotel.city }),
            ...(hotel?.country && { country: hotel.country }),
            ...(hotel?.country_code && { country_code: hotel.country_code }),
            ...(hotel?.stars && { stars: hotel.stars }),
        },
    };
}

/**
 * The hotel page's deal actions, ported from `DealComponent.react.js`: `handleOnBookClick` WITH its
 * `selectedDeal` analytics (spec §3.6), `handleOnShareClick` and `handleOnRemoveFromListClick`.
 * `displayedOrder` is the deal's 1-based position on the RM3 page (today's `displayed_deal_order_count`).
 */
export default function useRm3Book({
    hotel,
    profile,
    searchTerms,
    travelPolicy,
    contractClientCompanyId,
    reservationManipulation,
}) {
    const navigate = useNavigate();

    const book = useCallback(
        (deal, displayedOrder) => {
            if (deal && deal.is_lazy_recheck === true) {
                if (isAnonymousBlocked(profile, PERMISSION_PERFORM_RESERVATION)) {
                    redirectToLogin(profile);
                    return;
                }
                LazyRecheckActions.start(deal, hotel && hotel.id);
                return;
            }

            if (Config.dev_mode || profile?.is_arbi_tester) {
                deal.reservation_manipulation = reservationManipulation;
            }

            DealActions.chooseDeal(deal);

            Analytics.actions.interactions.selectedDeal(
                profile,
                destinationWithHotel(searchTerms, hotel),
                searchTerms,
                deal,
                deal.deal_index,
                HotelStore.getDealCount(),
                ResultsStore.getTimePassedSinceSearchSessionCreatedInMilliSeconds(),
                displayedOrder,
                HotelStore.getTotalDisplayedDealsCount(),
            );

            RouterWrapper.goToReservationPage(navigate);
        },
        [navigate, hotel, profile, searchTerms, reservationManipulation],
    );

    const addToList = useCallback(
        (deal) => {
            const travelPolicyExists = travelPolicy && !!(travelPolicy.default || travelPolicy.place);
            deal.place_id = searchTerms.destination.place.place_id;
            BasketActions.addHotelDeal(hotel, deal, travelPolicyExists, profile, contractClientCompanyId);
            Analytics.actions.interactions.addedToBasket(profile, hotel, deal);
        },
        [hotel, profile, searchTerms, travelPolicy, contractClientCompanyId],
    );

    const removeFromList = useCallback(
        (deal) => {
            BasketActions.removeHotelDeal(deal);
            Analytics.actions.interactions.removedFromBasket(hotel, deal);
        },
        [hotel],
    );

    return { book, addToList, removeFromList };
}
